import {
  useCallback, useEffect, useRef, useState,
} from 'react';

const timeout = 500;
const validKeys = [
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'n',
  'o',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '.',
];

function isValidKey(key: string) {
  return validKeys.indexOf(key) >= 0;
}

/**
 * It takes an array of prefixes and a string, and returns true if the string starts with one of the
 * prefixes and is followed by 10 digits
 * @param {string[]} prefixes - An array of strings that are the prefixes of the series of keys.
 * @param {string} str - The string to be validated.
 * @returns A boolean value.
 */
function isValidSeriesOfKeys(prefixes: string[], str: string) {
  const regexString = `^(${prefixes.join('|')})([0-9]{10})$`;
  return Boolean(str.match(RegExp(regexString, 'i')));
}

/**
 * It takes a list of prefixes and a string, and if the string starts with one of the prefixes followed
 * by 10 digits, it returns the prefix and the digits as a tuple
 * @param {string[]} prefixes - An array of strings that are the prefixes that the code can start with.
 * @param {string} str - The string to check.
 * @returns A tuple of two strings - ie prefix as the first value and the string as the second.
 */
function getCodeDetails(prefixes: string[], str: string) {
  const regexString = `^(${prefixes.join('|')})([0-9]{10})$`;
  const matches = str.match(RegExp(regexString, 'i'));
  if (!matches) {
    return null;
  }

  return [matches[1], matches[2]] as const;
}

/**
 * Determines if series of keys is partially valid but
 * maybe not complete as of yet
 * It returns true if the string is a valid series of keys, and false otherwise
 * @param {string[]} prefixes - an array of strings that are the prefixes of the series of keys
 * @param {string} str - The string to be tested.
 * @returns A boolean value.
 */
function isPartiallyValidSeriesOfKeys(prefixes: string[], str: string) {
  const regexString = '^([A-Z]{1,3})([0-9]{0,10})';
  return Boolean(str.match(RegExp(regexString, 'i')));
}

/**
 * @param prefix If not supplied will be disabled
 */
const useBarcodeScannerListener = (
  prefixes: string[],
  // eslint-disable-next-line no-unused-vars
  callback: (prefix: string, val: string) => void,
) => {
  const buffer = useRef('');
  const timer = useRef<any>(undefined);
  const isMounted = useRef(false);
  const input = useRef<HTMLInputElement | undefined>(undefined);

  // eslint-disable-next-line no-unused-vars
  const [qrString, setQRString] = useState<string>('');

  const _clearBuffer = () => {
    buffer.current = '';
    setQRString('');
  };

  const setScan = (scan: string) => {
    setQRString(scan);
    buffer.current = scan;
  };

  /* A callback function that is being used to listen for keypresses. */
  const _onKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (isMounted.current) {
        if (isValidKey(e.key)) {
          buffer.current += e.key;
          if (e.target !== window) {
            input.current = (e.target as HTMLInputElement) ?? undefined;
          }
        } else if (e.code === 'Enter') {
          if (isValidSeriesOfKeys(prefixes, buffer.current)) {
            e.preventDefault();
            const matches = getCodeDetails(prefixes, buffer.current);
            if (!matches) {
              // this should never occur but just in case back out since there is nothing we can do.
              _clearBuffer();
              return false;
            }

            callback(matches[0], matches[1]);
            setTimeout(() => {
              if (input.current && input.current instanceof HTMLInputElement) {
                Object.getOwnPropertyDescriptor(
                  window.HTMLInputElement.prototype,
                  'value',
                )?.set?.call(input.current, '');
                input.current.dispatchEvent(
                  new Event('change', { bubbles: true }),
                );
              }
            }, 250);
            return false;
          }

          return true;
        }
      }

      return true;
    },
    [callback, prefixes],
  );
  useEffect(() => {
    isMounted.current = true;
    window.addEventListener('keydown', _onKeyPress, false);
    return () => {
      isMounted.current = false;
      window.removeEventListener('keydown', _onKeyPress, false);
    };
  }, [_onKeyPress]);

  useEffect(() => {
    if (!buffer.current || qrString === '') {
      return;
    }
    /* Checking if the qrString is a valid series of keys. If it is, it will call the callback function
   with the prefix and the value. */
    if (isValidSeriesOfKeys(prefixes, qrString)) {
      // if (isValidSeriesOfKeys(prefixes, buffer.current)) {
      const matches = getCodeDetails(prefixes, qrString);
      if (!matches) {
        // this should never occur but just in case back out since there is nothing we can do.
        _clearBuffer();
        return;
      }
      callback(matches[0], matches[1]);
      setQRString('');
    }

    /* Clearing the timer. */
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }

    if (!isPartiallyValidSeriesOfKeys(prefixes, qrString)) {
      _clearBuffer();
    } else {
      timer.current = setTimeout(() => {
        _clearBuffer();
      }, timeout);
      // eslint-disable-next-line consistent-return
      return () => {
        if (timer) {
          clearTimeout(timer.current);
        }
      };
    }
  }, [callback, prefixes, qrString]);

  return [qrString, setScan] as const;
};

export default useBarcodeScannerListener;
